import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '../../context/AuthContext'
import { authClient } from '../../lib/authClient'
import { PROFILE_PATH } from '../../lib/nav-items'
import { Avatar } from '../atoms/Avatar'

const ROW_CLASS =
	'flex items-center gap-2.5 px-3 py-2.5 rounded-lg border-none text-sm cursor-pointer text-left w-full min-h-[44px] bg-transparent text-chrome-foreground/80 hover:text-chrome-foreground hover:bg-chrome-foreground/8 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome-foreground/80'

export function AccountMenu({ onNavigate }: { onNavigate?: () => void }) {
	const { user, signOut, hasPasskey, markPasskeyAdded } = useAuth()
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const [isAddingPasskey, setIsAddingPasskey] = useState(false)

	const isPasskeySupported = typeof window.PublicKeyCredential !== 'undefined'
	const isProfileActive = pathname === PROFILE_PATH
	const isProfileIncomplete = (user?.missing_profile_fields ?? []).length > 0

	async function addPasskey() {
		setIsAddingPasskey(true)
		const result = await authClient.passkey.addPasskey()
		setIsAddingPasskey(false)
		if (result?.error) {
			toast.error('Face ID could not be set up')
			return
		}
		markPasskeyAdded()
		toast.success('Face ID is set up for this device')
	}

	function openProfile() {
		navigate(PROFILE_PATH)
		onNavigate?.()
	}

	return (
		<div className="flex flex-col gap-0.5 mt-2 pt-3 border-t border-chrome-foreground/10">
			<button
				className={`${ROW_CLASS} ${isProfileActive ? 'text-chrome-foreground bg-chrome-foreground/10' : ''}`}
				aria-current={isProfileActive ? 'page' : undefined}
				onClick={openProfile}
			>
				<span className="relative shrink-0">
					<Avatar name={user?.name ?? ''} size="sm" className="bg-chrome-foreground/15 text-chrome-foreground" />
					{isProfileIncomplete && (
						<span className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-warning ring-2 ring-chrome" aria-hidden="true" />
					)}
				</span>
				<span className="flex min-w-0 flex-col">
					<span className="truncate text-chrome-foreground/90">{user?.name}</span>
					<span className="truncate text-xs">{isProfileIncomplete ? 'Complete your profile' : 'My profile'}</span>
				</span>
			</button>
			{isPasskeySupported && !hasPasskey && (
				<button className={ROW_CLASS} disabled={isAddingPasskey} onClick={addPasskey}>
					<i className="ti ti-fingerprint text-lg" aria-hidden="true" />
					Set up Face ID
				</button>
			)}
			<button className={ROW_CLASS} onClick={signOut}>
				<i className="ti ti-logout text-lg" aria-hidden="true" />
				Log out
			</button>
		</div>
	)
}
